import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { loadTBMetrics, refreshTBOutbreakOutputs, TB_COUNTRIES } from "../services/tbOutbreakApi";
import TBPlotlyPanel from "../components/tbOutbreak/TBPlotlyPanel";
import TBMapPanel from "../components/tbOutbreak/TBMapPanel";
import TBIndicators from "../components/tbOutbreak/TBIndicators";

import "./TBOutbreakPage.css";

function formatPValue(value) {
    if (value === null || value === undefined) {
        return "n/a";
    }

    if (value < 0.0001) {
        return "< 0.0001";
    }

    return value.toFixed(4);
}

function formatGeneratedAt(value) {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-GB");
}

export default function TBOutbreakPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedIso = searchParams.get("country") || "SE";
    const countryIso = Object.hasOwn(TB_COUNTRIES, selectedIso) ? selectedIso : "SE";
    const countryConfig = TB_COUNTRIES[countryIso];
    const countryName = countryConfig.label;
    const localName = countryConfig.localLabel;
    const filePrefix = countryConfig.country;
    const localPrefix = countryConfig.localScope;

    const [scope, setScope] = useState("all");
    const [metrics, setMetrics] = useState(null);
    const [metricsError, setMetricsError] = useState("");
    const [isCheckingResponses, setIsCheckingResponses] = useState(false);
    const [responseCheck, setResponseCheck] = useState(null);
    const [responseCheckError, setResponseCheckError] = useState("");
    const [contentVersion, setContentVersion] = useState(0);

    useEffect(() => {
        let active = true;
        setMetrics(null);
        setMetricsError("");
        setScope("all");
        setResponseCheck(null);
        setResponseCheckError("");

        loadTBMetrics(countryIso)
            .then((data) => {
                if (active) {
                    setMetrics(data);
                    setMetricsError("");
                }
            })
            .catch((error) => {
                console.error("Failed to load TB outbreak metrics:", error);

                if (active) {
                    setMetricsError(error.message);
                }
            });

        return () => {
            active = false;
        };
    }, [countryIso]);

    const currentMetrics = metrics?.meta?.country === filePrefix ? metrics : null;
    const summary = currentMetrics?.summary;
    const detection = currentMetrics?.detection;
    const local = currentMetrics?.local;
    const meta = currentMetrics?.meta;

    const concentration = local?.contrast_with_national;
    const growth = local?.growth;

    const showNational = scope === "all" || scope === "national";
    const showLocal = Boolean(local) && (scope === "all" || scope === "local");

    const displayedResponses =
        scope === "local"
            ? local?.responses
            : summary?.responses;

    const handleCheckNewResponses = async () => {
        if (isCheckingResponses) {
            return;
        }

        setIsCheckingResponses(true);
        setResponseCheckError("");

        try {
            const result = await refreshTBOutbreakOutputs(countryIso);

            setResponseCheck(result);

            if (result.outputs_updated) {
                try {
                    const refreshedMetrics = await loadTBMetrics(countryIso);

                    setMetrics(refreshedMetrics);
                    setMetricsError("");

                    // Force Plotly panels and Folium map to reload
                    // after the backend publishes the new output package.
                    setContentVersion((version) => version + 1);
                } catch (reloadError) {
                    console.error(
                        "TB outputs were updated, but the dashboard could not reload them:",
                        reloadError
                    );

                    setMetricsError(reloadError.message);
                    setResponseCheckError(
                        "Outputs were updated, but the dashboard could not reload the latest data."
                    );
                }
            }
        } catch (error) {
            console.error("Failed to refresh TB outbreak outputs:", error);

            setResponseCheck(null);
            setResponseCheckError(error.message);
        } finally {
            setIsCheckingResponses(false);
        }
    };

    return (
        <div className="pageContainer tb-outbreak-page">
            <header className="tb-page-header">
                <div className="tb-page-header-main">
                    <div className="tb-outbreak-eyebrow">
                        Participatory TB surveillance · {countryName} · {localName}
                    </div>

                    <h1 className="tb-outbreak-title">
                        TB Signal Monitor
                    </h1>

                    <p className="tb-outbreak-lede">
                        Symptom-screening responses scored against the confirmed
                        notification baseline to support early identification of a
                        potential tuberculosis outbreak.
                    </p>
                </div>

                <div className="tb-page-actions">
                    <label className="tb-country-selector">
                        Country{" "}
                        <select
                            value={countryIso}
                            disabled={isCheckingResponses}
                            onChange={(event) => setSearchParams({ country: event.target.value })}
                            aria-label="TB outbreak country"
                        >
                            {Object.entries(TB_COUNTRIES).map(([iso, config]) => (
                                <option key={iso} value={iso}>{config.label}</option>
                            ))}
                        </select>
                    </label>
                    <div
                        className="tb-scope-selector"
                        role="group"
                        aria-label="Dashboard scope"
                    >
                        <button
                            type="button"
                            className={scope === "all" ? "active" : ""}
                            aria-pressed={scope === "all"}
                            onClick={() => setScope("all")}
                        >
                            Everything
                        </button>

                        <button
                            type="button"
                            className={scope === "national" ? "active" : ""}
                            aria-pressed={scope === "national"}
                            onClick={() => setScope("national")}
                        >
                            {countryName}
                        </button>

                        <button
                            type="button"
                            className={scope === "local" ? "active" : ""}
                            aria-pressed={scope === "local"}
                            onClick={() => setScope("local")}
                        >
                            {localName}
                        </button>
                    </div>

                    <div className="tb-response-check-control">
                        <button
                            type="button"
                            className="tb-refresh-button"
                            onClick={handleCheckNewResponses}
                            disabled={isCheckingResponses}
                            aria-busy={isCheckingResponses}
                        >
                            <span
                                className="tb-refresh-button-icon"
                                aria-hidden="true"
                            >
                                ↻
                            </span>

                            {isCheckingResponses
                                ? "Checking & updating..."
                                : "Check for new responses"}
                        </button>

                        {responseCheck && (
                            <span
                                className={`tb-response-check-status ${
                                    responseCheck.outputs_updated
                                        ? "up-to-date"
                                        : responseCheck.has_new_responses
                                            ? "has-new"
                                            : "up-to-date"
                                }`}
                            >
                                {responseCheck.outputs_updated
                                    ? `Updated · ${responseCheck.new_responses} new ${
                                          responseCheck.new_responses === 1
                                              ? "response"
                                              : "responses"
                                      } processed`
                                    : responseCheck.has_new_responses
                                        ? `${responseCheck.new_responses} new ${
                                              responseCheck.new_responses === 1
                                                  ? "response"
                                                  : "responses"
                                          } available`
                                        : "Up to date"}
                            </span>
                        )}

                        {responseCheckError && (
                            <span className="tb-response-check-status error">
                                Check failed
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className="ds-btn tb-back-button"
                        onClick={() => navigate("/landing")}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </header>

            <div className="tb-run-summary">
                <div>
                    <span>Generated</span>
                    <strong>
                        {metrics
                            ? formatGeneratedAt(meta?.generated_at)
                            : metricsError
                                ? "Unavailable"
                                : "Loading..."}
                    </strong>
                </div>

                <div>
                    <span>Responses</span>
                    <strong>
                        {displayedResponses?.toLocaleString("en-GB") ??
                            (metricsError ? "Unavailable" : "Loading...")}
                    </strong>
                </div>

                <div>
                    <span>Period</span>
                    <strong>
                        {summary
                            ? `${summary.date_from} → ${summary.date_to}`
                            : metricsError
                                ? "Unavailable"
                                : "Loading..."}
                    </strong>
                </div>

                <div>
                    <span>Scope</span>
                    <strong>
                        {scope === "all"
                            ? "All"
                            : scope === "national"
                                ? countryName
                                : localName}
                    </strong>
                </div>
            </div>

            {showNational && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>Current signal</h2>
                        <p>Where the system stands right now.</p>
                    </div>

                    {metricsError ? (
                        <div className="tb-load-error">
                            <strong>
                                Could not load TB outbreak metrics.
                            </strong>
                            <span>{metricsError}</span>
                        </div>
                    ) : !summary ? (
                        <div className="tb-current-signal-panel">
                            Loading current signal...
                        </div>
                    ) : (
                        <div className="tb-current-signal-panel">
                            <div className="tb-signal-banner">
                                <div className="tb-signal-status">
                                    {summary.severity?.toUpperCase()}
                                </div>

                                <div className="tb-signal-content">
                                    <h3>{summary.headline}</h3>

                                    <p>
                                        <strong>
                                            {summary.latest_alerts}
                                        </strong>{" "}
                                        high-priority alerts in{" "}
                                        <strong>
                                            {summary.latest_month}
                                        </strong>{" "}
                                        against{" "}
                                        <strong>
                                            {summary.latest_expected}
                                        </strong>{" "}
                                        expected from the baseline rate applied
                                        to this month's{" "}
                                        <strong>
                                            {summary.latest_responses}
                                        </strong>{" "}
                                        responses — a{" "}
                                        <strong>
                                            {summary.latest_ratio}×
                                        </strong>{" "}
                                        excess{" "}
                                        (P(X ≥ observed){" "}
                                        {formatPValue(summary.latest_p)}).
                                    </p>

                                    {summary.first_breach_month && (
                                        <p className="tb-signal-secondary">
                                            First breach:{" "}
                                            <strong>
                                                {summary.first_breach_month}
                                            </strong>{" "}
                                            - {summary.first_breach_alerts} alerts
                                            against{" "}
                                            {summary.first_breach_expected} expected.
                                        </p>
                                    )}

                                    {detection && (
                                        <p className="tb-signal-method">
                                            Baseline model:{" "}
                                            <code>
                                                {detection.serfling_mode}
                                            </code>
                                            {" · "}
                                            Detectors:{" "}
                                            {detection.detectors_used?.join(", ") ||
                                                "—"}
                                            {" · "}
                                            Warning at p&lt;
                                            {detection.warning_threshold_p}
                                            {" · "}
                                            Signal at p&lt;
                                            {detection.signal_threshold_p}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="tb-metric-grid">
                                <div className="tb-metric-card">
                                    <span>Responses</span>

                                    <strong>
                                        {summary.responses.toLocaleString(
                                            "en-GB"
                                        )}
                                    </strong>

                                    <small>
                                        {summary.months} months ·{" "}
                                        {summary.date_from} to{" "}
                                        {summary.date_to}
                                    </small>
                                </div>

                                <div className="tb-metric-card">
                                    <span>High-priority alerts</span>
                                    <strong>{summary.alerts}</strong>

                                    <small>
                                        {summary.alert_share_pct}% of responses
                                    </small>
                                </div>

                                <div className="tb-metric-card">
                                    <span>Latest vs expected</span>

                                    <strong>
                                        {summary.latest_ratio}×
                                    </strong>

                                    <small>
                                        {summary.latest_expected} expected in{" "}
                                        {summary.latest_month}
                                    </small>
                                </div>

                                <div className="tb-metric-card">
                                    <span>Exposure-linked</span>

                                    <strong>
                                        {summary.exposure_linked}
                                    </strong>

                                    <small>
                                        Report contact with a known MDR-TB case
                                    </small>
                                </div>

                                {scope === "all" && concentration && (
                                    <div className="tb-metric-card">
                                        <span>
                                            {localName} concentration
                                        </span>

                                        <strong>
                                            {
                                                concentration.concentration_ratio
                                            }
                                            ×
                                        </strong>

                                        <small>
                                            {
                                                concentration.local_share_of_alerts_pct
                                            }
                                            % of alerts on{" "}
                                            {
                                                concentration.local_share_of_responses_pct
                                            }
                                            % of screening
                                        </small>
                                    </div>
                                )}

                                {scope === "all" &&
                                    growth?.doubling_time_weeks && (
                                        <div className="tb-metric-card">
                                            <span>Doubling time</span>

                                            <strong>
                                                {
                                                    growth.doubling_time_weeks
                                                }{" "}
                                                wk
                                            </strong>

                                            <small>
                                                +
                                                {
                                                    growth.weekly_growth_pct
                                                }
                                                % per week
                                            </small>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {(showNational || showLocal) && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>Where it is</h2>

                        <p>
                            The spatial picture: cases needing action and the
                            location identified by the scan.
                        </p>
                    </div>

                    <div
                        className={`tb-two-column-grid ${
                            scope === "all"
                                ? ""
                                : "tb-single-panel-grid"
                        }`}
                    >
                        {showNational && (
                            <TBMapPanel countryIso={countryIso}
                                key={`${countryIso}:tb-map-${contentVersion}`}
                                title="Cases requiring action"
                                scopeLabel={countryName}
                                description="Actionable screening signals, national spatial distribution and the scan-detected most likely cluster."
                            />
                        )}

                        {showLocal && (
                            <TBPlotlyPanel
                                key={`${countryIso}:district-breakdown-${contentVersion}`}
                                filename={`${filePrefix}_${localPrefix}_District_Breakdown_TB_Plotly.json`}
                                title="Alerts by district"
                                scopeLabel={localName}
                                description="Observed alerts by district compared with the number expected from each district's share of screening."
                            />
                        )}
                    </div>
                </section>
            )}

            {showNational && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>
                            How much, against what was expected
                        </h2>

                        <p>
                            Counts are interpreted beside the expectation that
                            accounts for screening volume.
                        </p>
                    </div>

                    <div className="tb-two-column-grid">
                        <TBPlotlyPanel
                            key={`${countryIso}:screening-alerts-${contentVersion}`}
                            filename={`${filePrefix}_Screening_Alerts_TB_Plotly.json`}
                            title="Screening alerts against expectation"
                            scopeLabel={countryName}
                            description="High-priority alerts per month compared with the number expected from the baseline."
                        />

                        <TBPlotlyPanel
                            key={`${countryIso}:screening-composition-${contentVersion}`}
                            filename={`${filePrefix}_Screening_Composition_TB_Plotly.json`}
                            title="Response composition"
                            scopeLabel={countryName}
                            description="Total responses, symptom-positive responses, exposure-linked responses and high-priority alerts over time."
                        />
                    </div>
                </section>
            )}

            {showLocal && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>How far and how fast</h2>

                        <p>
                            The two questions a local response team asks next.
                        </p>
                    </div>

                    <div className="tb-two-column-grid">
                        <TBPlotlyPanel
                            key={`${countryIso}:distance-decay-${contentVersion}`}
                            filename={`${filePrefix}_${localPrefix}_Distance_Decay_TB_Plotly.json`}
                            title="Alert rate by distance from the cluster centre"
                            scopeLabel={localName}
                            description="Alert share across concentric distance bands around the detected cluster centre."
                        />

                        <TBPlotlyPanel
                            key={`${countryIso}:weekly-curve-${contentVersion}`}
                            filename={`${filePrefix}_${localPrefix}_Weekly_Curve_TB_Plotly.json`}
                            title="Weekly local epidemic curve"
                            scopeLabel={localName}
                            description="Weekly alerts and exposure-linked responses alongside local screening volume."
                        />
                    </div>
                </section>
            )}

            {showNational && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>
                            Which settings and which contacts
                        </h2>

                        <p>
                            Denominator-adjusted excess by place and the contact
                            structure around the index case.
                        </p>
                    </div>

                    <div className="tb-two-column-grid">
                        <TBPlotlyPanel
                            key={`${countryIso}:setting-scan-${contentVersion}`}
                            filename={`${filePrefix}_Setting_Scan_TB_Plotly.json`}
                            title="Setting scan"
                            scopeLabel={countryName}
                            description="Observed alerts by screening setting compared with the number expected from each setting's share of screening."
                        />

                        <TBPlotlyPanel
                            key={`${countryIso}:exposure-links-${contentVersion}`}
                            filename={`${filePrefix}_Exposure_Links_TB_Plotly.json`}
                            title="Exposure links by relationship"
                            scopeLabel={countryName}
                            description="Exposure-linked responses grouped by reported relationship, with the alert share highlighted."
                        />
                    </div>
                </section>
            )}

            {showNational && (
                <section className="tb-section">
                    <div className="tb-section-heading">
                        <h2>Framework indicators</h2>

                        <p>
                            Screening indicators used to support escalation and
                            follow-up.
                        </p>
                    </div>

                    {metrics ? (
                        <TBIndicators
                            indicators={metrics.indicators}
                        />
                    ) : metricsError ? (
                        <div className="tb-load-error">
                            <strong>
                                Could not load framework indicators.
                            </strong>

                            <span>{metricsError}</span>
                        </div>
                    ) : (
                        <div className="tb-indicators-empty">
                            Loading framework indicators...
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
